import { createContext, useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import MDButton from "components/MDButton";

const FeedbackContext = createContext(null);

/** Thông báo nhỏ góc màn hình và hộp thoại xác nhận dùng chung cho mọi trang. */
export function FeedbackProvider({ children }) {
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const notify = useMemo(() => {
    const show = (severity) => (message) =>
      setToast({
        severity,
        message: message instanceof Error ? message.message : String(message),
        key: Date.now(),
      });
    return { success: show("success"), error: show("error"), info: show("info") };
  }, []);

  const confirm = useCallback(
    (options) => new Promise((resolve) => setConfirmState({ ...options, resolve })),
    []
  );

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.key}
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === "error" ? 8000 : 4000}
        onClose={(event, reason) => reason !== "clickaway" && setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ color: "#fff", whiteSpace: "pre-line" }}
          >
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
      <Dialog
        open={Boolean(confirmState)}
        onClose={() => closeConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmState?.title || "Xác nhận"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {confirmState?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => closeConfirm(false)}>
            Huỷ
          </MDButton>
          <MDButton
            variant="gradient"
            color={confirmState?.color || "info"}
            onClick={() => closeConfirm(true)}
          >
            {confirmState?.confirmText || "Đồng ý"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
}

FeedbackProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNotify() {
  return useContext(FeedbackContext).notify;
}

export function useConfirm() {
  return useContext(FeedbackContext).confirm;
}
